/**
 * Weekly Challenges — 260 rotating challenges (5 years).
 * Auto-cycles: week_number = floor(daysSinceEpoch / 7) % 260
 * Reference epoch: 2026-03-09 (Season 1 start, Monday)
 */

import { supabase } from './supabase';
import { createLogger } from './logger';

const log = createLogger('CHALLENGES');

const EPOCH = new Date('2026-03-09T00:00:00Z').getTime();
const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;
const TOTAL_CHALLENGES = 260;

export interface WeeklyChallenge {
  id: string;
  week_number: number;
  title: string;
  description: string;
  category: string;
  target_lift: string | null;
  target_metric: string | null;
  target_value: number | null;
  scoring_method: string;
}

export interface ChallengeSubmission {
  id: string;
  challenge_id: string;
  user_id: string;
  score: number;
  video_url: string | null;
  submitted_at: string;
}

export interface ChallengeLeaderboardEntry {
  user_id: string;
  handle: string | null;
  display_name: string | null;
  score: number;
  rank: number;
  video_url: string | null;
}

/**
 * Get the current week number (1-indexed, cycles 1-260).
 */
export function getCurrentWeekNumber(): number {
  const now = Date.now();
  const weeksSinceEpoch = Math.floor((now - EPOCH) / MS_PER_WEEK);
  return (weeksSinceEpoch % TOTAL_CHALLENGES) + 1;
}

/**
 * Get week start and end dates for a given week number offset from now.
 */
export function getWeekDates(weekOffset = 0): { start: Date; end: Date } {
  const now = Date.now();
  const weeksSinceEpoch = Math.floor((now - EPOCH) / MS_PER_WEEK) + weekOffset;
  const start = new Date(EPOCH + weeksSinceEpoch * MS_PER_WEEK);
  const end = new Date(start.getTime() + MS_PER_WEEK - 1);
  return { start, end };
}

/**
 * Fetch the current week's challenge.
 */
export async function getCurrentChallenge(): Promise<WeeklyChallenge | null> {
  const weekNum = getCurrentWeekNumber();
  const { data } = await supabase
    .from('weekly_challenges')
    .select('*')
    .eq('week_number', weekNum)
    .single();

  return data as WeeklyChallenge | null;
}

/**
 * Submit a score for the current challenge.
 */
export async function submitChallengeScore(
  challengeId: string,
  userId: string,
  score: number,
  videoUrl?: string,
  strengthEventId?: string,
): Promise<{ success: boolean; error: string | null }> {
  const { error } = await supabase
    .from('challenge_submissions')
    .upsert(
      {
        challenge_id: challengeId,
        user_id: userId,
        score,
        video_url: videoUrl ?? null,
        strength_event_id: strengthEventId ?? null,
        submitted_at: new Date().toISOString(),
      },
      { onConflict: 'challenge_id,user_id' },
    );

  if (error) return { success: false, error: error.message };
  return { success: true, error: null };
}

/**
 * Get leaderboard for a challenge.
 */
export async function getChallengeLeaderboard(
  challengeId: string,
  limit = 50,
): Promise<ChallengeLeaderboardEntry[]> {
  const { data: submissions } = await supabase
    .from('challenge_submissions')
    .select('user_id, score, video_url')
    .eq('challenge_id', challengeId)
    .order('score', { ascending: false })
    .limit(limit);

  if (!submissions?.length) return [];

  const userIds = submissions.map(s => s.user_id);
  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, handle, display_name')
    .in('id', userIds);

  const profileMap = new Map((profiles ?? []).map(p => [p.id, p]));

  return submissions.map((s, i) => {
    const p = profileMap.get(s.user_id);
    return {
      user_id: s.user_id,
      handle: p?.handle ?? null,
      display_name: p?.display_name ?? null,
      score: s.score,
      rank: i + 1,
      video_url: s.video_url,
    };
  });
}

/**
 * Check if user has submitted for a challenge.
 */
export async function getUserSubmission(
  challengeId: string,
  userId: string,
): Promise<ChallengeSubmission | null> {
  const { data } = await supabase
    .from('challenge_submissions')
    .select('*')
    .eq('challenge_id', challengeId)
    .eq('user_id', userId)
    .single();

  return data as ChallengeSubmission | null;
}

/* ── Past Challenges (Pro feature) ── */

export interface PastChallenge {
  id: string;
  title: string;
  winner_name: string | null;
}

/**
 * Fetch recently completed challenges with their top scorers.
 * "Past" = week_number < current week_number in the rotation.
 */
export async function getPastChallenges(limit = 4): Promise<PastChallenge[]> {
  const currentWeek = getCurrentWeekNumber();
  // Get challenges from recent past weeks
  const pastWeeks = Array.from({ length: limit }, (_, i) => {
    const w = currentWeek - (i + 1);
    return w < 1 ? w + TOTAL_CHALLENGES : w;
  });

  const { data: challenges } = await supabase
    .from('weekly_challenges')
    .select('id, title, week_number')
    .in('week_number', pastWeeks)
    .order('week_number', { ascending: false })
    .limit(limit);

  if (!challenges?.length) return [];

  // Get top scorer for each challenge
  const results: PastChallenge[] = [];
  for (const ch of challenges) {
    const { data: topSub } = await supabase
      .from('challenge_submissions')
      .select('score, profiles!inner(display_name, handle)')
      .eq('challenge_id', ch.id)
      .order('score', { ascending: false })
      .limit(1);

    const winner = topSub?.[0];
    const winnerProfile = winner?.profiles as any;
    results.push({
      id: ch.id,
      title: ch.title,
      winner_name: winnerProfile?.display_name || winnerProfile?.handle || null,
    });
  }

  return results;
}
